import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Droplets, Mountain, TreePine } from "lucide-react";

const TinchaFalls = () => {
  const navigate = useNavigate();
  const waterfallImage = "/Indore/waterfalls/tincha0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Tincha Falls",
    "description": "Hidden gem waterfall near Indore, perfect for monsoon visits and nature photography",
    "image": waterfallImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Tincha Village",
      "addressLocality": "Indore District",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 07:00-17:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.3",
      "reviewCount": "2800"
    }
  };

  const highlights = [
    {
      icon: <Droplets className="w-6 h-6" />,
      title: "Pristine Waterfall",
      description: "Beautiful cascade falling from rocky cliffs surrounded by untouched natural beauty"
    },
    {
      icon: <Mountain className="w-6 h-6" />,
      title: "Secluded Location",
      description: "Hidden gem away from crowds, offering peaceful and intimate nature experience"
    },
    {
      icon: <TreePine className="w-6 h-6" />,
      title: "Lush Surroundings",
      description: "Dense forest cover and rich vegetation creating a perfect natural environment"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Photography Paradise",
      description: "Excellent spot for nature photography with scenic landscapes and wildlife"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free (No entry charges)" },
    { label: "Best Season", value: "July to November (Post-monsoon)" },
    { label: "Timings", value: "7:00 AM - 5:00 PM" },
    { label: "Duration", value: "3-5 hours" },
    { label: "Trek Difficulty", value: "Easy to Moderate" },
    { label: "Distance from Indore", value: "45 km (1.5 hour drive)" }
  ];

  const seasonalInfo = [
    {
      season: "Monsoon (July-September)",
      description: "Waterfall at full flow, lush green surroundings, but requires caution",
      pros: ["Maximum water flow", "Vibrant greenery", "Cool weather"],
      cons: ["Slippery paths", "Heavy rains", "Limited accessibility"]
    },
    {
      season: "Post-Monsoon (October-November)",
      description: "Best time to visit with good water flow and safer conditions",
      pros: ["Safe trekking", "Good water flow", "Pleasant weather"],
      cons: ["Slightly crowded", "Water level decreasing"]
    },
    {
      season: "Winter (December-February)",
      description: "Clear weather but reduced water flow, good for trekking",
      pros: ["Clear skies", "Safe conditions", "Good for camping"],
      cons: ["Less water flow", "Dry surroundings"]
    }
  ];

  const activities = [
    {
      title: "Nature Trekking",
      description: "Explore scenic trails leading to the waterfall through forest paths",
      duration: "2-3 hours",
      difficulty: "Moderate"
    },
    {
      title: "Photography",
      description: "Capture stunning shots of the cascade and surrounding wilderness",
      duration: "2-4 hours",
      difficulty: "Easy"
    },
    {
      title: "Bird Watching",
      description: "Spot various bird species in the rich forest ecosystem",
      duration: "1-3 hours",
      difficulty: "Easy"
    },
    {
      title: "Camping",
      description: "Experience overnight camping under the stars (with permission)",
      duration: "12+ hours",
      difficulty: "Moderate"
    }
  ];

  const nearbyAttractions = [
    { name: "Patalpani Waterfall", distance: "20 km", time: "30 mins" },
    { name: "Choral Dam", distance: "30 km", time: "45 mins" },
    { name: "Mhow Cantonment", distance: "25 km", time: "35 mins" },
    { name: "Indore City", distance: "45 km", time: "90 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Tincha Falls Near Indore - Hidden Waterfall Paradise | Aaoghumen</title>
        <meta name="description" content="Discover Tincha Falls near Indore with Aaoghumen - a hidden waterfall gem perfect for monsoon trips, trekking, and peaceful nature experiences. Complete guide with seasonal info and activities." />
        <meta name="keywords" content="tincha falls, tincha waterfall indore, hidden waterfall indore, indore monsoon destinations, aaoghumen tincha, waterfall trekking indore, tincha falls photography, secret waterfalls madhya pradesh" />
        
        <meta property="og:title" content="Tincha Falls - Hidden Waterfall Near Indore | Aaoghumen" />
        <meta property="og:description" content="Explore the hidden beauty of Tincha Falls, a pristine waterfall near Indore perfect for nature lovers seeking tranquil monsoon experiences." />
        <meta property="og:image" content={waterfallImage} />
        <meta name="twitter:image" content={waterfallImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/tincha-falls" />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/tincha-falls" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-teal-600 via-green-500 to-emerald-600 overflow-hidden">
          <img
            src={waterfallImage}
            alt="Tincha Falls"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-teal-300">Tincha Falls</span>
              </h1>
              <p className="text-xl mb-6">
                Discover the hidden beauty of Tincha Falls, a pristine waterfall tucked away in the forests near Indore, offering tranquil monsoon experiences and untouched natural splendor.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.3 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>7 AM - 5 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Tincha Falls</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Tincha Falls represents one of the region's best-kept secrets, a pristine waterfall hidden in the dense forests near Indore that offers visitors an authentic wilderness experience away from the crowds. Unlike more popular waterfalls in the area, Tincha Falls maintains its natural charm and untouched beauty, making it a perfect destination for those seeking solitude and a genuine connection with nature. The waterfall cascades gracefully over rocky terrain, creating natural pools and a serene atmosphere that exemplifies the raw beauty of Madhya Pradesh's landscape.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The journey to Tincha Falls is an adventure in itself, taking visitors through winding forest paths and rural landscapes that showcase the region's biodiversity. The area surrounding the falls is rich in flora and fauna, with dense vegetation that comes alive during the monsoon season. The relatively untouched nature of this location means visitors can experience the forest ecosystem in its most natural state, with opportunities to spot various bird species and small wildlife.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, discover this hidden gem through guided treks that ensure both safety and environmental consciousness. Learn about the local ecosystem, practice responsible tourism, and experience the peaceful tranquility that makes Tincha Falls a special destination for nature enthusiasts, photographers, and anyone looking to escape into the wilderness while staying relatively close to Indore.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Falls Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-teal-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-teal-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Seasonal Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Seasonal Guide</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {seasonalInfo.map((season, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-teal-600 mb-3">{season.season}</h3>
                  <p className="text-gray-600 mb-4">{season.description}</p>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-green-600 mb-2">Pros:</h4>
                      <ul className="text-sm text-gray-600">
                        {season.pros.map((pro, proIndex) => (
                          <li key={proIndex}>• {pro}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-red-600 mb-2">Cons:</h4>
                      <ul className="text-sm text-gray-600">
                        {season.cons.map((con, conIndex) => (
                          <li key={conIndex}>• {con}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Activities */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Activities & Experiences</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {activities.map((activity, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-teal-600 mb-3">{activity.title}</h3>
                  <p className="text-gray-600 mb-4">{activity.description}</p>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Duration: {activity.duration}</span>
                    <span>Difficulty: {activity.difficulty}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Preparation Guide */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Preparation Guide</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Essential Gear</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Waterproof trekking shoes</li>
                    <li>• Quick-dry clothing</li>
                    <li>• Rain jacket/poncho</li>
                    <li>• Backpack with rain cover</li>
                    <li>• First aid kit</li>
                    <li>• Headlamp/flashlight</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Photography Kit</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Camera with weather protection</li>
                    <li>• Extra batteries</li>
                    <li>• Tripod for long exposures</li>
                    <li>• Lens cleaning kit</li>
                    <li>• Neutral density filters</li>
                    <li>• Waterproof camera bag</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Supplies</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Sufficient water (2-3 liters)</li>
                    <li>• Energy bars and snacks</li>
                    <li>• Packed lunch if camping</li>
                    <li>• Insect repellent</li>
                    <li>• Sunscreen</li>
                    <li>• Waste disposal bags</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Safety Guidelines */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Safety Guidelines</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-xl p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3">Before You Go</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Check weather conditions</li>
                    <li>• Inform someone about your travel plans</li>
                    <li>• Carry emergency contact numbers</li>
                    <li>• Start early to avoid afternoon heat</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3">During the Trek</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Stay on designated paths</li>
                    <li>• Travel in groups, avoid solo trekking</li>
                    <li>• Be cautious near water during monsoon</li>
                    <li>• Respect wildlife and maintain distance</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-teal-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-green-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Limited Options:</strong> Public transport not directly available</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus to Mhow:</strong> Then hire private vehicle</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Most practical option from Indore</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Route:</strong> Indore → Mhow → Tincha Village</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Distance:</strong> 45 km from Indore (1.5 hours)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Road Condition:</strong> Good till village, then walking trail</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-teal-600 to-green-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Discover Hidden Paradise</h2>
              <p className="text-xl mb-6">Explore Tincha Falls with experienced guides and sustainable tourism practices</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Tincha Village, MP</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-teal-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Hidden Adventure
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-teal-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default TinchaFalls;